import React from "react";
import { useTranslation } from "react-i18next";
import { PlaceSuggestion } from "../../../../api/meLocation.service";

export interface PlaceResultsProps {
  query: string;
  results: PlaceSuggestion[];
  searching: boolean;
  searchError: boolean;
  signedIn: boolean;
  /** The result being resolved to a place, if any. */
  resolvingId: string | null;
  resolveError: boolean;
  locateError: boolean;
  onSelect: (result: PlaceSuggestion) => void;
  /** Keeps focus on the input while a result is tapped. */
  onMouseDown: (event: React.MouseEvent) => void;
  /** The result name with the typed text in bold. */
  highlight: (name: string) => React.ReactNode;
}

// Live search results dropdown for the active field.
const PlaceResults: React.FC<PlaceResultsProps> = ({
  query,
  results,
  searching,
  searchError,
  signedIn,
  resolvingId,
  resolveError,
  locateError,
  onSelect,
  onMouseDown,
  highlight,
}) => {
  const { t } = useTranslation("hailing");
  return (
    <div className="mt-2 overflow-hidden rounded-2xl bg-white p-1.5 shadow-[0_8px_24px_rgba(15,21,26,0.1)]">
      {searching ? (
        <div className="px-2.5 py-3 text-[13px] text-[#6B7075]">
          {t("steps.where.searching")}
        </div>
      ) : results.length > 0 ? (
        results.map((r) => (
          <button
            key={r.placeId}
            type="button"
            onMouseDown={onMouseDown}
            onClick={() => onSelect(r)}
            disabled={resolvingId !== null}
            className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left hover:bg-[#F2F3F3] focus-visible:bg-[#F2F3F3] focus-visible:outline-none disabled:opacity-60"
          >
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[#ECECEC]">
              <span className="font-material-symbols text-[18px] leading-none text-ink-100">
                {resolvingId === r.placeId ? "progress_activity" : "location_on"}
              </span>
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] text-[#3A4045]">
                {highlight(r.mainText)}
              </span>
              {r.secondaryText ? (
                <span className="mt-0.5 block truncate text-[12px] text-[#6B7075]">
                  {r.secondaryText}
                </span>
              ) : null}
            </span>
          </button>
        ))
      ) : (
        <div className="px-2.5 py-3 text-[13px] text-[#6B7075]">
          {searchError
            ? t("steps.where.searchError")
            : !signedIn && query
              ? t("steps.where.signInToSearch")
              : t("steps.where.noResults")}
        </div>
      )}
      {resolveError && (
        <div className="px-2.5 py-3 text-[13px] text-danger-ink">
          {t("steps.where.resolveError")}
        </div>
      )}
      {locateError && (
        <div className="px-2.5 py-3 text-[13px] text-danger-ink">
          {t("steps.where.locateError")}
        </div>
      )}
    </div>
  );
};

export default PlaceResults;
